import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

const conversions: Record<string, Record<string, number>> = {
  cup: { cup: 1, tbsp: 16, tsp: 48, ml: 236.588, oz: 8, gram: 236.588 },
  tbsp: { cup: 0.0625, tbsp: 1, tsp: 3, ml: 14.787, oz: 0.5, gram: 14.787 },
  tsp: { cup: 0.0208, tbsp: 0.333, tsp: 1, ml: 4.929, oz: 0.167, gram: 4.929 },
  ml: { cup: 0.00423, tbsp: 0.0676, tsp: 0.203, ml: 1, oz: 0.0338, gram: 1 },
  oz: { cup: 0.125, tbsp: 2, tsp: 6, ml: 29.574, oz: 1, gram: 28.35 },
  gram: { cup: 0.00423, tbsp: 0.0676, tsp: 0.203, ml: 1, oz: 0.0353, gram: 1 }
};

export default function CookingConverter() {
  const [amount, setAmount] = useState("");
  const [fromUnit, setFromUnit] = useState("cup");
  const [toUnit, setToUnit] = useState("ml");
  const [result, setResult] = useState("");

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Lifestyle", url: "https://calclab.app/lifestyle" },
      { name: "Cooking Converter", url: "https://calclab.app/calculator/cooking-converter" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Cooking Measurement Converter",
      "Convert cooking measurements between cups, tablespoons, teaspoons, ml, and more. Free online kitchen converter.",
      "https://calclab.app/calculator/cooking-converter",
      lang
    )
  ]);

  const convert = () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) return;
    
    const converted = val * conversions[fromUnit][toUnit];
    setResult(converted.toFixed(2));
  };

  const units = [
    { value: "cup", label: "Cups" },
    { value: "tbsp", label: "Tablespoons (tbsp)" },
    { value: "tsp", label: "Teaspoons (tsp)" },
    { value: "ml", label: "Milliliters (ml)" },
    { value: "oz", label: "Fluid Ounces (oz)" },
    { value: "gram", label: "Grams (g)" }
  ];

  return (
    <PageLayout calculatorName="Cooking Measurement Converter" calculatorPath="/calculator/cooking-converter">
      <div className="grid gap-8 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Convert Measurements</CardTitle>
            <CardDescription>For cooking and baking</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Amount</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="1" step="0.01" />
            </div>
            <div>
              <Label>From</Label>
              <Select value={fromUnit} onValueChange={setFromUnit}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {units.map(u => (
                    <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>To</Label>
              <Select value={toUnit} onValueChange={setToUnit}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {units.map(u => (
                    <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={convert} className="w-full">Convert</Button>
          </CardContent>
        </Card>

        {result && (
          <Card>
            <CardHeader>
              <CardTitle>Result</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center">
                <div className="text-5xl font-bold text-primary mb-4">{result}</div>
                <div className="text-lg text-muted-foreground">
                  {units.find(u => u.value === toUnit)?.label}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PageLayout>
  );
}
